import React, { useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import MetricCardDesigned from 'components/MetricCardDesigned';
import TimeSpanningChart from 'components/TimeSpanningChart';
import SavingsOpportunities from 'components/SavingsOpportunities';
import PieResultDesigned from 'components/PieResultDesigned';

// Import both data sets
import * as emptyMockData from '../data/emptyMockData';
import * as overviewMockData from '../data/overviewMockData';

const Overview: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Determine which dataset to use based on URL query parameter
  const dashboardData = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const useMockData = searchParams.has('mock');
    
    // Use overviewMockData if ?mock is in the URL, otherwise use emptyMockData
    const data = useMockData ? overviewMockData : emptyMockData;
    
    return {
      icons: data.icons,
      mainMetrics: data.mainMetrics,
      smallMetrics: data.smallMetrics,
      resourceUtilizationData: data.resourceUtilizationData,
      resourceUtilizationSeries: data.resourceUtilizationSeries,
      errorData: data.errorData,
      errorSeries: data.errorSeries,
      savingsOpportunities: data.savingsOpportunities,
      pieChartData: data.pieChartData
    };
  }, []);

  // Destructure data for easier access
  const { 
    icons, 
    mainMetrics, 
    smallMetrics, 
    resourceUtilizationData, 
    resourceUtilizationSeries,
    errorData,
    errorSeries,
    savingsOpportunities,
    pieChartData 
  } = dashboardData;

  const getIconSrc = (iconName: string) => {
    const iconKey = iconName + (isDark ? 'Transparent' : '');
    return icons[iconKey as keyof typeof icons] || '';
  };

  return (
    <div className={`${isDark ? "dashboard-container-dark" : "dashboard-container"} flex-grow overflow-y-auto`} style={{ height: 'calc(100vh - 106px)', marginTop: '20px' }}>
      <div className="p-4 max-w-[2000px] mx-auto w-full pt-4">          
        {/* First row with 4 cards - matches the width of 6 cards below */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {mainMetrics.map((metric, index) => (
            <div key={index} className="h-full">
              <MetricCardDesigned 
                title={metric.title} 
                subtitle={metric.subtitle}
                titleValue={metric.titleValue}
                subtitleValue={metric.subtitleValue}
                titlePrefix={metric.titlePrefix}
                titleSuffix={metric.titleSuffix}
                smallBoxValue={metric.smallBoxValue}
                smallBoxSuffix={metric.smallBoxSuffix}
                smallBoxPrefix={metric.smallBoxPrefix}
                fill={metric.fill as 'yellowFlarion' | 'white'}
                className="h-full"
                iconSrc={getIconSrc(metric.icon)}
              />
            </div>
          ))}
        </div>
        
        {/* Second row with 6 cards using small size */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 mb-10">
          {smallMetrics.map((metric, index) => (
            <div key={index} className="h-full">
              <MetricCardDesigned 
                title={metric.title} 
                titleValue={metric.titleValue}
                titleSuffix={metric.titleSuffix}
                fill={metric.fill as 'yellowFlarion' | 'white'}
                size="small"
                className="h-full"
                iconSrc={getIconSrc(metric.icon)}
              />
            </div>
          ))}
        </div>
        
        {/* Charts row - side by side with fixed height */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <div className="h-[500px]">
            <TimeSpanningChart 
              title="Resource"
              subtitle="Utilization"
              type="line"
              data={resourceUtilizationData}
              series={resourceUtilizationSeries}
              yAxisLabel="Utilization"
              yAxisUnit="%"
              yAxisDomain={[0, 100]}
              yAxisTicks={[0, 25, 50, 75, 100]}
            />
          </div>
          <div className="h-[500px]">
            <TimeSpanningChart 
              title="Errors"
              subtitle="Over Time"
              type="bar"
              data={errorData}
              series={errorSeries}
              yAxisLabel="Errors"
              yAxisDomain={[0, 28]}
              yAxisTicks={[0, 7, 14, 21, 28]}
              stacked={true}
            />
          </div>
        </div>

        {/* Savings Opportunities and Pie Charts row - conditional layout based on savings data */}
        {savingsOpportunities.length > 0 ? (
          // Show Savings Opportunities (2/3 width) + Pie Charts (1/3 width stacked)
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            {/* Savings Opportunities - 2/3 width */}
            <div className="lg:col-span-2 h-[780px]">
              <SavingsOpportunities 
                opportunities={savingsOpportunities}
                fontSize={24} 
              />
            </div>
            
            {/* Pie Charts Column - 1/3 width */}
            <div className="grid grid-cols-1 gap-2">
              {/* First Pie Chart */}
              <div className="h-[250px] w-full mb-2">
                <PieResultDesigned
                  title="Job Status"
                  types={pieChartData.jobStatus}
                />
              </div>
              
              {/* Second Pie Chart */}
              <div className="h-[250px] w-full mb-2">
                <PieResultDesigned
                  title="Job Cost"
                  types={pieChartData.jobCost}
                />
              </div>
              
              {/* Third Pie Chart */}
              <div className="h-[250px] w-full">
                <PieResultDesigned
                  title="Job Duration"
                  types={pieChartData.jobDuration}
                />
              </div>
            </div>
          </div>
        ) : (
          // No savings opportunities - show 3 pie charts side by side
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            {/* First Pie Chart */}
            <div className="h-[300px]">
              <PieResultDesigned
                title="Job Status"
                types={pieChartData.jobStatus}
              />
            </div>
            
            {/* Second Pie Chart */}
            <div className="h-[300px]">
              <PieResultDesigned
                title="Job Cost"
                types={pieChartData.jobCost}
              />
            </div>
            
            {/* Third Pie Chart */}
            <div className="h-[300px]">
              <PieResultDesigned
                title="Job Duration"
                types={pieChartData.jobDuration}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;